import { createSelector, createSlice } from '@reduxjs/toolkit';

import { userUpdate } from 'FRONTEND/reducers/userSlice';
import { APPOINTMENT_STATUS_FILTER_LIST, APPOINTMENT_TYPE_LIST, EVENT_KIND_LIST } from 'FRONTEND/utils/constants';
import { getDeserializedState } from 'FRONTEND/utils/serialization';
import { CALENDAR_VIEWS } from 'LEGACY/constants';

const REDUCER_NAME = 'calendar';

const storedState = getDeserializedState()?.[REDUCER_NAME];
const initialState = {
  currentDay: new Date().toJSON(),
  isMounted: false,
  isAppointmentListMounted: false,
  actorId: storedState?.actorId || null,
  actorType: storedState?.actorType || null,
  typeFilterList: storedState?.typeFilterList || APPOINTMENT_TYPE_LIST,
  eventKindFilterList: storedState?.eventKindFilterList || EVENT_KIND_LIST,
  appointmentStatusFilterList: storedState?.appointmentStatusFilterList || APPOINTMENT_STATUS_FILTER_LIST,
  selectedEventId: null,
  selectedEventExpertId: null,
  selectedEventActorId: null,
  isUnrestrictedMode: false, // No need to save this in localStorage, currently only changes when parsys_cloud.modules doesn't have ['rtc'] in the config. See RightPanel.jsx
  view: storedState?.view || CALENDAR_VIEWS.WEEK,
  isAppointmentModalOpen: false,
};

/**
 * This slice holds state regarding the Calendar and AppointmentList features, such as the currently viewed expert or the applied filters.
 */
const calendarSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    updateCurrentDay(state, { payload: { currentDay } }) {
      state.currentDay = currentDay;
    },
    mounted(state) {
      state.isMounted = true;
    },
    unmounted(state) {
      state.isMounted = false;
    },
    appointmentListMounted(state) {
      state.isAppointmentListMounted = true;
    },
    appointmentListUnmounted(state) {
      state.isAppointmentListMounted = false;
    },
    typeFilterChange(state, { payload: { typeFilterList } }) {
      state.typeFilterList = typeFilterList;
    },
    eventKindFilterChange(state, { payload: { eventKindFilterList } }) {
      state.eventKindFilterList = eventKindFilterList;
    },
    appointmentStatusFilterChange(state, { payload: { appointmentStatusFilterList } }) {
      state.appointmentStatusFilterList = appointmentStatusFilterList;
    },
    pickEvent(state, { payload: { eventId, expertId, resourceId, actorId } }) {
      state.selectedEventActorId = actorId;
      state.selectedEventId = eventId;
    },
    goToActorCalendar(state, { payload: { actorId, actorType } }) {
      state.actorId = actorId;
      state.actorType = actorType;
    },
    goToActorSettings(state, { payload: { actorId, actorType } }) {
      state.actorId = actorId;
      state.actorType = actorType;
    },
    goToMultipleCalendar(state) {
      state.actorId = null;
      state.actorType = null;
    },
    goToGivenSlotForm(state) {},
    goToGivenExpertForm(state, { payload: { id } = {} }) {
      state.actorId = id;
    },
    goToLocationForm(state, { payload }) {
      state.actorId = null;
    },
    goToUnrestrictedForm(state, { payload }) {
      state.actorId = null;
    },
    goToPatientForm(state, { payload }) {
      state.actorId = null;
    },
    goBackToForm(state) {},
    changeUnrestrictedMode(state, { payload: { isUnrestrictedMode } }) {
      state.isUnrestrictedMode = isUnrestrictedMode;
    },
    changeCalendarView(state, { payload: { view } }) {
      state.view = view;
    },
    toggleAppointmentModal(state, { payload: { isOpen } }) {
      state.isAppointmentModalOpen = isOpen;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userUpdate, (state, { payload: { managedExperstList } }) => {
      if (managedExperstList?.length === 1 && !state.resourceId) {
        state.expertId = managedExperstList[0].id;
      }
    });
  },
});

export const selectTypeFilterList = (state) => state.calendar.typeFilterList;
export const selectEventKindFilterList = (state) => state.calendar.eventKindFilterList;
export const selectAppointmentStatusFilterList = (state) => state.calendar.appointmentStatusFilterList;
export const selectCalendarActorId = (state) => {
  return state.calendar.actorId;
};
export const selectCurrentDay = createSelector(
  (state) => state.calendar.currentDay,
  (currentDay) => new Date(currentDay),
);

export const {
  updateCurrentDay,
  mounted,
  unmounted,
  appointmentListMounted,
  appointmentListUnmounted,
  typeFilterChange,
  eventKindFilterChange,
  appointmentStatusFilterChange,
  pickEvent,
  goToActorCalendar,
  goToActorSettings,
  goToMultipleCalendar,
  goToGivenSlotForm,
  goToGivenExpertForm,
  goToLocationForm,
  goToUnrestrictedForm,
  goBackToForm,
  changeUnrestrictedMode,
  changeCalendarView,
  toggleAppointmentModal,
  goToPatientForm,
} = calendarSlice.actions;
export default calendarSlice.reducer;
